import * as React from "react";
import { FileCheck2, Plus } from "lucide-react";
import type { DocumentScanDraft, DriverProfile, Expense, ExpenseCategory, GstTreatment } from "@/types/tax";
import { expenseCategoryLabels } from "@/data/atoRules";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Dropdown, type DropdownOption } from "@/components/ui/Dropdown";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/Field";
import { Input, Textarea } from "@/components/ui/Input";
import { PdfPreview } from "@/components/domain/PdfPreview";
import { claimableGstForExpense, expenseGstComponent } from "@/lib/gst";
import { currency, isoToday, makeId, parseMoney } from "@/lib/utils";

const categoryOptions = Object.entries(expenseCategoryLabels).map(([value, label]) => ({ value, label })) as DropdownOption<ExpenseCategory>[];

interface ExpenseFormProps {
  userId: string;
  profile: DriverProfile;
  draft?: DocumentScanDraft | null;
  editingExpense?: Expense | null;
  onSave: (expense: Expense) => void;
  onCancelEdit?: () => void;
}

export function ExpenseForm({ userId, profile, draft, editingExpense, onSave, onCancelEdit }: ExpenseFormProps) {
  const [date, setDate] = React.useState(isoToday());
  const [category, setCategory] = React.useState<ExpenseCategory>("fuel");
  const [supplier, setSupplier] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [gstTreatment, setGstTreatment] = React.useState<GstTreatment>("gst_included");
  const [manualGstAmount, setManualGstAmount] = React.useState("");
  const [businessUsePercent, setBusinessUsePercent] = React.useState(String(profile.defaultBusinessUsePercent || 100));
  const [scanMeta, setScanMeta] = React.useState<{ sourceDocumentId: string; fileName: string } | null>(null);

  React.useEffect(() => {
    if (!draft) return;
    const expense = draft.expense || {};
    if (expense.date) setDate(String(expense.date));
    if (isExpenseCategory(expense.category)) setCategory(expense.category);
    setSupplier(String(expense.supplier || ""));
    setDescription(String(expense.description || ""));
    setAmount(moneyInput(expense.amount));
    if (isGstTreatment(expense.gstTreatment)) setGstTreatment(expense.gstTreatment);
    setManualGstAmount(moneyInput(expense.manualGstAmount));
    setBusinessUsePercent(String(expense.businessUsePercent || profile.defaultBusinessUsePercent || 100));
    setScanMeta({ sourceDocumentId: draft.sourceDocumentId, fileName: draft.fileName });
  }, [draft, profile.defaultBusinessUsePercent]);

  React.useEffect(() => {
    if (!editingExpense) return;
    setDate(editingExpense.date);
    setCategory(editingExpense.category);
    setSupplier(editingExpense.supplier || "");
    setDescription(editingExpense.description || "");
    setAmount(moneyInput(editingExpense.amount));
    setGstTreatment(editingExpense.gstTreatment);
    setManualGstAmount(moneyInput(editingExpense.manualGstAmount));
    setBusinessUsePercent(String(editingExpense.businessUsePercent || profile.defaultBusinessUsePercent || 100));
    setScanMeta(
      editingExpense.entrySource === "document_scan" && editingExpense.sourceDocumentId
        ? { sourceDocumentId: editingExpense.sourceDocumentId, fileName: "Existing scanned source" }
        : null,
    );
  }, [editingExpense, profile.defaultBusinessUsePercent]);

  const previewExpense: Expense = {
    id: "preview",
    userId,
    date,
    category,
    supplier,
    description,
    amount: parseMoney(amount),
    gstTreatment,
    manualGstAmount: parseMoney(manualGstAmount),
    businessUsePercent: parseMoney(businessUsePercent),
    hasTaxInvoice: true,
    entrySource: scanMeta ? "document_scan" : editingExpense?.entrySource || "manual",
    sourceDocumentId: scanMeta?.sourceDocumentId || editingExpense?.sourceDocumentId || null,
  };
  const gstComponent = expenseGstComponent(previewExpense);
  const claimable = claimableGstForExpense(previewExpense);

  function resetForm() {
    setDate(isoToday());
    setCategory("fuel");
    setSupplier("");
    setDescription("");
    setAmount("");
    setGstTreatment("gst_included");
    setManualGstAmount("");
    setBusinessUsePercent(String(profile.defaultBusinessUsePercent || 100));
    setScanMeta(null);
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (previewExpense.amount <= 0) return;
    onSave({ ...previewExpense, id: editingExpense?.id || makeId("exp") });
    resetForm();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{editingExpense ? "Edit expense" : "Add expense"}</CardTitle>
        <CardDescription>GST credits are estimated from the amount and business-use percentage.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-5" onSubmit={submit}>
          {scanMeta ? (
            <div className="grid gap-3 rounded-lg border border-border bg-surface p-3">
              <div className="flex items-start gap-3">
                <FileCheck2 className="mt-0.5 size-5 shrink-0 text-credit" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-ink">Scanned draft loaded</p>
                  <p className="mt-1 text-sm text-muted">
                    Review the extracted fields against the source file, then add expense.
                  </p>
                </div>
              </div>
              <PdfPreview documentId={scanMeta.sourceDocumentId} fileName={scanMeta.fileName} display="compact" />
            </div>
          ) : null}
          <FieldGroup className="grid gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="expense-date">Date</FieldLabel>
              <Input id="expense-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
            </Field>
            <Field>
              <FieldLabel>Category</FieldLabel>
              <Dropdown label="Expense category" value={category} options={categoryOptions} onChange={setCategory} />
            </Field>
            {category === "car_purchase" ? (
              <div className="md:col-span-2">
                <Alert variant="warning" title="Car limit applies">
                  Car limit is $69,883. GST credits and depreciation claims may be capped, so review the car purchase rules before using this amount.
                </Alert>
              </div>
            ) : null}
            <Field>
              <FieldLabel htmlFor="supplier">Supplier</FieldLabel>
              <Input id="supplier" value={supplier} onChange={(event) => setSupplier(event.target.value)} placeholder="Supplier name" />
            </Field>
            <Field>
              <FieldLabel htmlFor="amount">Amount including GST if charged</FieldLabel>
              <Input id="amount" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0.00" />
            </Field>
            <Field>
              <FieldLabel htmlFor="business-use">Business use</FieldLabel>
              <Input
                id="business-use"
                inputMode="numeric"
                value={businessUsePercent}
                onChange={(event) => setBusinessUsePercent(event.target.value)}
              />
              <FieldDescription>Only this percentage of GST is included.</FieldDescription>
            </Field>
            <Field className="md:col-span-2">
              <FieldLabel htmlFor="description">Description</FieldLabel>
              <Textarea id="description" value={description} onChange={(event) => setDescription(event.target.value)} />
            </Field>
          </FieldGroup>

          <div className="rounded-lg border border-border bg-surface p-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <Preview label="GST component" value={currency(gstComponent)} />
              <Preview label="Claimable GST" value={currency(claimable)} tone={claimable > 0 ? "credit" : "muted"} />
              <Preview label="Business use" value={`${previewExpense.businessUsePercent || 0}%`} />
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="submit">
              <Plus className="size-4" aria-hidden="true" />
              {editingExpense ? "Update expense" : "Add expense"}
            </Button>
            {editingExpense ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  resetForm();
                  onCancelEdit?.();
                }}
              >
                Cancel
              </Button>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function isExpenseCategory(value: unknown): value is ExpenseCategory {
  return typeof value === "string" && value in expenseCategoryLabels;
}

function isGstTreatment(value: unknown): value is GstTreatment {
  return typeof value === "string" && ["gst_included", "no_gst", "supplier_not_registered", "manual_gst", "mixed_invoice"].includes(value);
}

function moneyInput(value: unknown) {
  const amount = parseMoney(typeof value === "number" ? value : String(value || ""));
  return amount > 0 ? String(amount) : "";
}

function Preview({ label, value, tone = "default" }: { label: string; value: string; tone?: "default" | "credit" | "muted" }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted">{label}</p>
      <p className={tone === "credit" ? "tabular mt-1 font-semibold text-credit" : tone === "muted" ? "tabular mt-1 font-semibold text-muted" : "tabular mt-1 font-semibold text-ink"}>
        {value}
      </p>
    </div>
  );
}
